/*
  Cart dynamic recommendations

  - Reads the live cart from /cart.js
  - Empty cart    -> block hidden
  - Otherwise     -> recommendations for the cart products (newest first),
                     complementary first then related, merged up to MAX_ITEMS,
                     excluding anything already in the cart and (if possible)
                     products of the same product type as the cart items
  - Nothing found -> fallback collection (minus cart items), or hidden
  - Re-runs after any add / change / update / clear cart request, and whenever
    the theme re-renders or resets the block

  Debug: add ?recs_debug=1 to the URL, then check the browser console.
*/
(() => {
  const SELECTOR = '[data-dynamic-recs]';
  const INTENTS = ['complementary', 'related']; // complementary = set manually in Search & Discovery
  const MAX_SEEDS = 3; // how many cart products to base recommendations on
  const MAX_ITEMS = 8; // how many cards to show
  const EXCLUDE_SAME_TYPE = true; // skip products of the same product type as cart items (e.g. no more armchairs when an armchair is in the cart)
  const FETCH_LIMIT = 10; // Shopify max is 10
  const CART_TTL = 1500; // ms to reuse a /cart.js response
  const KEY_ATTR = 'data-recs-key'; // set by this script; if the theme resets the block it disappears and we re-run
  const CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  let DEBUG = false;
  try {
    DEBUG = /[?&]recs_debug=1/.test(location.search) || localStorage.getItem('recs_debug') === '1';
    if (/[?&]recs_debug=1/.test(location.search)) localStorage.setItem('recs_debug', '1');
    if (/[?&]recs_debug=0/.test(location.search)) localStorage.removeItem('recs_debug');
  } catch (e) {}
  const log = (...args) => DEBUG && console.log('[cart-recs]', ...args);

  /* ---------- open / closed state (collapsible bar) ---------- */
  const OPEN_KEY = 'cart_recs_open';
  let openState = false; // closed by default
  try {
    openState = sessionStorage.getItem(OPEN_KEY) === '1';
  } catch (e) {}

  function applyOpen(el) {
    el.classList.toggle('is-open', openState);
    const toggle = el.querySelector('[data-recs-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', String(openState));
  }

  function setOpen(value) {
    openState = value;
    try {
      sessionStorage.setItem(OPEN_KEY, value ? '1' : '0');
    } catch (e) {}
    document.querySelectorAll('[data-dynamic-recs]').forEach((el) => {
      applyOpen(el);
      if (value) setTimeout(() => updateArrows(el), 320); // after the expand animation
    });
  }

  // "8 items · from $49" under the title
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
  function formatMoney(cents) {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(amount % 1 === 0 ? 0 : 2);
    }
  }

  function updateSubtitle(el, items) {
    const subtitle = el.querySelector('[data-recs-subtitle]');
    if (!subtitle) return;
    const prices = items.map((i) => i.price).filter((p) => Number.isFinite(p) && p > 0);
    const count = `${items.length} ${items.length === 1 ? 'item' : 'items'}`;
    if (!prices.length) {
      subtitle.textContent = count;
      return;
    }
    const min = Math.min(...prices);
    subtitle.textContent = items.length === 1 ? `${count} · ${formatMoney(min)}` : `${count} · from ${formatMoney(min)}`;
  }

  // Small thumbnails in the closed bar
  function updatePreview(el, items) {
    const preview = el.querySelector('[data-recs-preview]');
    if (!preview) return;
    preview.replaceChildren(
      ...items.slice(0, 3).map((item) => {
        const source = item.node.querySelector('img');
        const img = document.createElement('img');
        if (source) {
          img.src = source.currentSrc || source.getAttribute('src') || '';
          if (source.getAttribute('srcset')) img.srcset = source.getAttribute('srcset');
          img.sizes = '30px';
        }
        img.alt = '';
        img.loading = 'lazy';
        return img;
      })
    );
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-recs-toggle]');
    if (!toggle || !toggle.closest('[data-dynamic-recs]')) return;
    event.preventDefault();
    setOpen(!openState);
  });

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);
  const recCache = new Map();
  let cartRequest = null;
  let cartRequestAt = 0;

  /* ---------- data ---------- */

  function getCart(force) {
    if (force || !cartRequest || Date.now() - cartRequestAt > CART_TTL) {
      cartRequestAt = Date.now();
      cartRequest = nativeFetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null);
    }
    return cartRequest;
  }

  function productIdOf(node) {
    let id = node.getAttribute('data-la-dn-product-id') || node.getAttribute('data-product-id');
    if (!id) {
      const inner = node.querySelector('[data-product-id], [data-la-dn-product-id]');
      if (inner) id = inner.getAttribute('data-product-id') || inner.getAttribute('data-la-dn-product-id');
    }
    return id ? String(id) : '';
  }

  function toItems(nodes) {
    return Array.from(nodes)
      .filter((n) => n.nodeType === 1)
      .map((node) => ({
        id: productIdOf(node),
        type: (node.getAttribute('data-recs-product-type') || '').trim().toLowerCase(),
        price: parseInt(node.getAttribute('data-recs-price'), 10),
        node: node.cloneNode(true),
      }));
  }

  function fetchRecs(productId, intent, sectionId) {
    const key = `${productId}|${intent}|${sectionId}`;
    if (recCache.has(key)) return recCache.get(key);

    const url =
      `${root}recommendations/products?product_id=${encodeURIComponent(productId)}` +
      `&limit=${FETCH_LIMIT}&intent=${intent}&section_id=${encodeURIComponent(sectionId)}`;

    const request = nativeFetch(url, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) log('recommendations request failed', res.status, url);
        return res.ok ? res.text() : '';
      })
      .then((html) => {
        if (!html) return [];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrap = doc.querySelector('[data-recs-items]');
        if (!wrap) log('no [data-recs-items] in response – is the section file named cart-product-recommendations.liquid?', url);
        const items = wrap ? toItems(wrap.children) : [];
        log(`${intent} for ${productId}:`, items.map((i) => i.id));
        return items;
      })
      .catch((err) => {
        log('recommendations error', err);
        return [];
      });

    recCache.set(key, request);
    return request;
  }

  /* ---------- rendering ---------- */

  function hide(el, key) {
    el.hidden = true;
    el.style.display = 'none';
    el.setAttribute(KEY_ATTR, key);
  }

  function show(el, items, key) {
    const grid = el.querySelector('[data-recs-grid]');
    if (!grid) return log('no [data-recs-grid] inside block');
    // importNode (not cloneNode) so the theme's <quick-add> custom elements upgrade and wire their click handlers
    grid.replaceChildren(...items.map((i) => document.importNode(i.node, true)));
    el.hidden = false;
    el.style.display = '';
    el.setAttribute(KEY_ATTR, key);
    applyOpen(el);
    updatePreview(el, items);
    updateSubtitle(el, items);
    grid.scrollLeft = 0;
    requestAnimationFrame(() => updateArrows(el));
    // If the drawer was closed while rendering, recheck once it has a size
    if (window.ResizeObserver && !grid._recsResize) {
      grid._recsResize = new ResizeObserver(() => updateArrows(el));
      grid._recsResize.observe(grid);
    }
    document.dispatchEvent(new CustomEvent('cart-recommendations:loaded', { detail: { element: el } }));
  }

  /* ---------- arrows ---------- */

  function updateArrows(el) {
    const grid = el.querySelector('[data-recs-grid]');
    if (!grid) return;
    const max = grid.scrollWidth - grid.clientWidth;
    const prev = el.querySelector('[data-recs-prev]');
    const next = el.querySelector('[data-recs-next]');
    // Class goes on the block itself; arrows live in the header next to the title
    el.classList.toggle('cart-recs--scrollable', max > 2);
    if (prev) prev.disabled = grid.scrollLeft <= 2;
    if (next) next.disabled = grid.scrollLeft >= max - 2;
  }

  // Arrow clicks: move one "page" (the visible cards) at a time
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-recs-prev], [data-recs-next]');
    if (!button) return;
    const el = button.closest(SELECTOR);
    const grid = el && el.querySelector('[data-recs-grid]');
    if (!grid) return;
    event.preventDefault();
    const direction = button.hasAttribute('data-recs-next') ? 1 : -1;
    grid.scrollBy({ left: direction * grid.clientWidth, behavior: 'smooth' });
  });

  // Keep arrow states in sync while scrolling/swiping (scroll doesn't bubble, so capture it)
  document.addEventListener(
    'scroll',
    (event) => {
      const grid = event.target;
      if (grid && grid.matches && grid.matches('[data-recs-grid]')) {
        const el = grid.closest(SELECTOR);
        if (el) updateArrows(el);
      }
    },
    true
  );

  // Drawer width changes (resize / drawer opening animation)
  window.addEventListener('resize', () => document.querySelectorAll(SELECTOR).forEach(updateArrows));
  ['cart:open', 'drawerOpen'].forEach((name) =>
    document.addEventListener(name, () =>
      setTimeout(() => document.querySelectorAll(SELECTOR).forEach(updateArrows), 350)
    )
  );

  async function render(el, forceCart) {
    const run = (el._recsRun || 0) + 1;
    el._recsRun = run;
    const stale = () => el._recsRun !== run || !el.isConnected;

    const cart = await getCart(forceCart);
    if (stale()) return;

    const lines = (cart && cart.items) || [];
    const inCart = [...new Set(lines.map((l) => String(l.product_id)))]; // newest first
    const key = inCart.join(',') || 'empty';
    log('cart products:', inCart);

    if (!inCart.length) return hide(el, key);

    const sectionId = el.dataset.sectionId || 'cart-product-recommendations';
    const cartTypes = new Set(
      lines.map((l) => String(l.product_type || '').trim().toLowerCase()).filter(Boolean)
    );

    // Pick up to MAX_ITEMS; first pass skips same-type products, second pass (only if
    // the first found nothing) allows them so the block never ends up empty for no reason.
    const pick = async (skipSameType) => {
      const picked = [];
      const seen = new Set(inCart);
      const take = (items) => {
        let added = 0;
        for (const item of items) {
          if (picked.length >= MAX_ITEMS) break;
          if (!item.id || seen.has(item.id)) continue;
          if (skipSameType && item.type && cartTypes.has(item.type)) continue;
          seen.add(item.id);
          picked.push(item);
          added++;
        }
        return added;
      };

      for (const seed of inCart.slice(0, MAX_SEEDS)) {
        if (picked.length >= MAX_ITEMS) break;
        for (const intent of INTENTS) {
          const recs = await fetchRecs(seed, intent, sectionId); // cached after the first call
          if (stale()) return null;
          if (take(recs) > 0) break; // got results for this product, move to the next one
        }
      }
      if (!picked.length) take(el._recsFallback || []);
      return picked;
    };

    let picked = await pick(EXCLUDE_SAME_TYPE && cartTypes.size > 0);
    if (picked === null) return;
    if (!picked.length && EXCLUDE_SAME_TYPE && cartTypes.size > 0) {
      log('only same-type products found, allowing them');
      picked = await pick(false);
      if (picked === null) return;
    }

    log('showing:', picked.map((i) => i.id));
    picked.length ? show(el, picked, key) : hide(el, key);
  }

  /* ---------- wiring ---------- */

  function init(el) {
    // Grid currently holds server-rendered HTML (fallback collection) – keep a copy
    const grid = el.querySelector('[data-recs-grid]');
    el._recsFallback = grid ? toItems(grid.children) : [];
    el.setAttribute(KEY_ATTR, 'pending');
    applyOpen(el);
    render(el, false);
  }

  function scan() {
    document.querySelectorAll(SELECTOR).forEach((el) => {
      if (!el.hasAttribute(KEY_ATTR)) init(el); // new block, or the theme reset it
    });
  }

  let refreshTimer = null;
  function refreshAll() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      log('cart changed, refreshing');
      getCart(true);
      document.querySelectorAll(SELECTOR).forEach((el) => (el.hasAttribute(KEY_ATTR) ? render(el, false) : init(el)));
    }, 200);
  }

  // Re-run after cart requests made with fetch
  window.fetch = function (input, ...rest) {
    const request = nativeFetch(input, ...rest);
    try {
      const url = typeof input === 'string' ? input : input && input.url;
      if (url && CART_URL_RE.test(String(url))) request.then(refreshAll, () => {});
    } catch (e) {}
    return request;
  };

  // ...and with XMLHttpRequest (jQuery / older apps)
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    try {
      if (url && CART_URL_RE.test(String(url))) this.addEventListener('loadend', refreshAll);
    } catch (e) {}
    return nativeOpen.call(this, method, url, ...rest);
  };

  // Theme events, if the theme fires them
  ['cart:updated', 'cart:change', 'cart:build', 'cart:open', 'ajaxProduct:added'].forEach((name) =>
    document.addEventListener(name, refreshAll)
  );

  // Pick up blocks that are added, replaced or reset by the theme
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  });

  function start() {
    log('started, blocks found:', document.querySelectorAll(SELECTOR).length);
    scan();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [KEY_ATTR],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();